import { RootState } from '@/store';

export interface SessionSyncMessage {
  type: 'STATE_UPDATE';
  payload: {
    tournament: RootState['tournament'];
    clock: RootState['clock'];
    players: RootState['players'];
    settings: RootState['settings'];
  };
  timestamp: number;
  windowId: string;
}

export class SessionSyncManager {
  private channel: BroadcastChannel;
  private windowId: string;
  private onStateUpdate?: (state: SessionSyncMessage['payload']) => void;
  private lastSyncTime: number = 0;
  private debounceTimeout?: number;

  constructor() {
    this.windowId = crypto.randomUUID();
    this.channel = new BroadcastChannel('tournament-sync');
    this.setupMessageListener();
    this.loadInitialState();
  }

  private setupMessageListener() {
    this.channel.addEventListener('message', (event: MessageEvent<SessionSyncMessage>) => {
      const { data } = event;
      
      // Ignore messages from this window
      if (data.windowId === this.windowId) return;
      
      // Only process newer state updates
      if (data.timestamp > this.lastSyncTime && this.onStateUpdate) {
        this.lastSyncTime = data.timestamp;
        this.onStateUpdate(data.payload);
      }
    });
  }

  private loadInitialState() {
    try {
      const storedState = sessionStorage.getItem('tournament-state');
      if (storedState && this.onStateUpdate) {
        const parsedState = JSON.parse(storedState);
        this.onStateUpdate(parsedState);
      }
    } catch (error) {
      console.warn('Failed to load initial state from session storage:', error);
    }
  }

  public setStateUpdateCallback(callback: (state: SessionSyncMessage['payload']) => void) {
    this.onStateUpdate = callback;
  }

  public broadcastState(state: SessionSyncMessage['payload']) {
    // Debounce rapid state updates
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    this.debounceTimeout = window.setTimeout(() => {
      const timestamp = Date.now();
      
      // Store in session storage
      try {
        sessionStorage.setItem('tournament-state', JSON.stringify(state));
      } catch (error) {
        console.warn('Failed to save state to session storage:', error);
      }

      // Broadcast to other windows
      const message: SessionSyncMessage = {
        type: 'STATE_UPDATE',
        payload: state,
        timestamp,
        windowId: this.windowId,
      };

      this.channel.postMessage(message);
      this.lastSyncTime = timestamp;
    }, 100); // 100ms debounce
  }

  public destroy() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.channel.close();
  }
}